<script>
  import { headerNavItems } from '../data/landing.js';
  import { token, logout, isLoggingOut } from '../stores/account.js';

  // variant:
  //   'default' — обычный сайт: лого + nav-меню + max-link + mobile-hamburger.
  //   'account' — личный кабинет: только лого (на /) и кнопка «Выход» справа.
  //               Без навигации лендинга и без max-link, чтобы не отвлекать
  //               пользователя от его билета.
  export let variant = 'default';

  let isMenuOpen = false;
  // Реактивно отражаем состояние сессии: login через AccountAuthSection
  // выставит token → кнопка появится без перезагрузки; logout очистит token
  // → кнопка спрячется. Сам token инициализируется из sessionStorage в
  // момент загрузки модуля (stores/account.js), поэтому на старте Header
  // тоже знает корректное значение.
  $: isAuthenticated = !!$token;
  $: isAccountVariant = variant === 'account';

  const toggleMenu = () => {
    isMenuOpen = !isMenuOpen;
  };

  const closeMenu = () => {
    isMenuOpen = false;
  };
</script>

<header class="site-header {isMenuOpen ? 'menu-open' : ''}" class:account-variant={isAccountVariant}>
  <a
    class="logo"
    href={isAccountVariant ? '/' : '#top'}
    aria-label="Индустрия ИИ"
    on:click={closeMenu}
  >
    <span class="logo-word">Индустрия</span>
    <span class="logo-mark">ИИ</span>
  </a>

  <div class="header-actions">
    {#if !isAccountVariant}
      <nav class="desktop-nav" aria-label="Главная навигация">
        {#each headerNavItems as item}
          <a href={item.href} on:click={closeMenu}><span>{item.label}</span></a>
        {/each}
      </nav>

      {#if isAuthenticated}
        <a class="account-link" href="/account" on:click={closeMenu} aria-label="Личный кабинет">
          <svg class="account-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle cx="12" cy="8" r="4" stroke="currentColor" stroke-width="2"/>
            <path d="M4 21c0-4 4-6 8-6s8 2 8 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
          <span>Личный кабинет</span>
        </a>
      {/if}

      <a class="max-link" href="https://max.ru/id540324189394_biz" target="_blank" rel="noreferrer" on:click={closeMenu}>
        <span>Канал в max</span>
        <span class="link-arrow" aria-hidden="true"></span>
      </a>

      <button class="menu-button" type="button" aria-label="Открыть меню" aria-expanded={isMenuOpen} on:click={toggleMenu}>
        <span></span>
        <span></span>
        <span></span>
      </button>
    {:else if isAuthenticated}
      <button
        class="logout-btn"
        type="button"
        on:click={logout}
        disabled={$isLoggingOut}
        aria-label="Выйти из кабинета"
      >
        <span class="logout-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4M15 8l4 4-4 4M19 12H9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <span>Выход</span>
      </button>
    {/if}
  </div>

  {#if !isAccountVariant}
    <nav class="mobile-nav" aria-label="Мобильная навигация">
      {#each headerNavItems as item}
        <a href={item.href} on:click={closeMenu}><span>{item.label}</span></a>
      {/each}
      {#if isAuthenticated}
        <a class="mobile-account" href="/account" on:click={closeMenu}><span>Личный кабинет</span></a>
      {/if}
      <a class="mobile-max" href="https://max.ru/id540324189394_biz" target="_blank" rel="noreferrer" on:click={closeMenu}><span>Канал в max</span></a>
    </nav>
  {/if}
</header>

<style>
  .site-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    display: flex;
    width: 100%;
    min-height: 76px;
    align-items: flex-start;
    justify-content: space-between;
    padding: 24px 64px 12px;
    background: rgba(0, 0, 0, 0.5);
    color: #ffffff;
    box-sizing: border-box;
  }

  .logo,
  .desktop-nav a,
  .max-link,
  .mobile-nav a {
    color: inherit;
    text-decoration: none;
  }

  .logo {
    display: inline-flex;
    align-items: center;
    align-self: stretch;
    gap: 7.857px;
    min-width: 0;
  }

  .logo-word {
    display: inline-flex;
    align-items: center;
    font-size: 22.857px;
    font-weight: 700;
    line-height: normal;
    white-space: nowrap;
  }

  .logo-mark {
    display: inline-flex;
    width: 40px;
    height: 40px;
    flex: 0 0 40px;
    align-items: center;
    justify-content: center;
    background: #e7b962;
    color: #000000;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 22.857px;
    font-weight: 700;
    line-height: normal;
    white-space: nowrap;
  }

  .header-actions,
  .desktop-nav,
  .max-link {
    display: flex;
    align-items: center;
  }

  .header-actions {
    gap: 80px;
  }

  .desktop-nav {
    gap: 24px;
    justify-content: flex-end;
  }

  .desktop-nav a {
    font-size: 18px;
    font-weight: 500;
    line-height: 25.14px;
    white-space: nowrap;
  }

  .max-link {
    gap: 12px;
    min-height: 44px;
    padding: 13px 23px;
    border-radius: 5px;
    font-size: 18px;
    font-weight: 500;
    line-height: 18px;
    text-transform: uppercase;
    white-space: nowrap;
    box-sizing: border-box;
  }

  .account-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 44px;
    padding: 10px 16px;
    border: 1px solid rgba(231, 185, 98, 0.55);
    border-radius: 8px;
    color: #e7b962;
    font-size: 15px;
    font-weight: 500;
    line-height: 1;
    text-decoration: none;
    white-space: nowrap;
    box-sizing: border-box;
    transition: border-color 160ms ease, background-color 160ms ease, color 160ms ease;
  }

  .account-link:hover,
  .account-link:focus-visible {
    border-color: #ffa800;
    color: #ffffff;
    background: rgba(255, 168, 0, 0.08);
  }

  .account-icon {
    width: 18px;
    height: 18px;
    flex: 0 0 auto;
  }

  .mobile-account {
    color: #e7b962;
  }

  @media (max-width: 1279px) {
    /* На tablet/mobile рендерится только в выпадающем меню (.mobile-account),
       а кнопка верхнего уровня скрывается — иначе на 360–390px она вместе
       с логотипом и бургером выезжает за viewport. */
    .account-link {
      display: none;
    }
  }

  .link-arrow {
    display: inline-block;
    width: 8px;
    height: 16px;
    background: currentColor;
    -webkit-mask: url('/icons/ui-icon-chevron-right.svg') center / contain no-repeat;
    mask: url('/icons/ui-icon-chevron-right.svg') center / contain no-repeat;
  }

  .menu-button {
    display: none;
    width: 50px;
    height: 50px;
    flex: 0 0 50px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin: 0;
    padding: 0 11px;
    border: 1px solid #ffa800;
    border-radius: 16px;
    background: #000000;
    cursor: pointer;
    box-sizing: border-box;
  }

  .menu-button span {
    display: block;
    width: 23px;
    height: 2px;
    border-radius: 1px;
    background: #ffffff;
    /* Полоски гамбургера — не текст. Глобальный 1px-сдвиг для button-spans
       к ним не относится; явно обнуляем transform в idle-state. */
    transform: none;
    transition: opacity 160ms ease, transform 160ms ease;
  }

  .menu-open .menu-button span:nth-child(1) {
    transform: translateY(8px) rotate(45deg);
  }

  .menu-open .menu-button span:nth-child(2) {
    opacity: 0;
  }

  .menu-open .menu-button span:nth-child(3) {
    transform: translateY(-8px) rotate(-45deg);
  }

  /* === Account variant: logout button === */

  .logout-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 10px;
    background: transparent;
    color: rgba(255, 255, 255, 0.7);
    font-family: Manrope, Roboto, Arial, sans-serif;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: border-color 160ms ease, color 160ms ease, background-color 160ms ease;
  }

  .logout-btn:hover:not([disabled]),
  .logout-btn:focus-visible:not([disabled]) {
    border-color: #ffa800;
    color: #ffffff;
    background: rgba(255, 168, 0, 0.05);
  }

  .logout-btn[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .logout-icon {
    display: inline-flex;
    align-items: center;
  }

  .logout-icon svg {
    width: 16px;
    height: 16px;
  }

  .desktop-nav a:hover,
  .desktop-nav a:focus-visible,
  .max-link:hover,
  .max-link:focus-visible,
  .mobile-nav a:hover,
  .mobile-nav a:focus-visible {
    color: #e7b962;
    text-decoration: none;
  }

  .mobile-nav {
    position: absolute;
    top: calc(100% + 1px);
    right: 32px;
    display: flex;
    width: min(320px, calc(100vw - 64px));
    flex-direction: column;
    gap: 8px;
    padding: 18px;
    border: 1px solid rgba(231, 185, 98, 0.45);
    border-radius: 16px;
    background: rgba(0, 0, 0, 0.94);
    opacity: 0;
    pointer-events: none;
    transform: translateY(-8px);
    transition: opacity 160ms ease, transform 160ms ease;
    box-sizing: border-box;
  }

  .menu-open .mobile-nav {
    opacity: 1;
    pointer-events: auto;
    transform: translateY(0);
  }

  .mobile-nav a {
    display: flex;
    min-height: 40px;
    align-items: center;
    color: #ffffff;
    font-size: 18px;
    font-weight: 500;
    line-height: 1.3;
  }

  .mobile-max {
    color: #e7b962 !important;
    text-transform: uppercase;
  }

  @media (max-width: 1279px) {
    .site-header {
      min-height: 86px;
      padding: 24px 32px 12px;
    }

    .header-actions {
      gap: 16px;
    }

    .desktop-nav {
      display: none;
    }

    .menu-button {
      display: inline-flex;
    }
  }

  @media (max-width: 767px) {
    .site-header {
      min-height: 74px;
      padding: 12px 12px 12px 24px;
    }

    .max-link {
      display: none;
    }

    .mobile-nav {
      right: 12px;
      width: min(320px, calc(100vw - 24px));
    }
  }
</style>
